import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';

const AppContext = createContext(null);

// Total paused milliseconds for a session; open pause interval counts up to nowMs
export const pausedMs = (session, nowMs = Date.now()) => {
  let total = 0;
  for (const iv of session?.paused_intervals || []) {
    if (!iv.pause_start) continue;
    const start = new Date(iv.pause_start).getTime();
    const end = iv.pause_end ? new Date(iv.pause_end).getTime() : nowMs;
    total += Math.max(0, end - start);
  }
  return total;
};

export const sessionIsPaused = (session) => {
  const ivs = session?.paused_intervals || [];
  return ivs.length > 0 && !ivs[ivs.length - 1].pause_end;
};

export const AppProvider = ({ children }) => {
  const [authed, setAuthed] = useState(!!localStorage.getItem('token'));
  const [theme, setThemeState] = useState(localStorage.getItem('theme') || 'dark');
  const [videoTheme, setVideoThemeState] = useState(localStorage.getItem('videoTheme') || 'original');
  const [stagesData, setStagesData] = useState({ stages: [], time_categories: [], task_categories: [] });
  const [activeSession, setActiveSession] = useState(null);
  const [elapsed, setElapsed] = useState(0); // ACTIVE seconds only (pauses excluded)
  const tickRef = useRef(null);

  const isPaused = sessionIsPaused(activeSession);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  const setTheme = (t) => setThemeState(t);

  // Background video theme ('original' | 'cinematic') — persisted, exposed on <html> for CSS scoping
  useEffect(() => {
    document.documentElement.setAttribute('data-video-theme', videoTheme);
    localStorage.setItem('videoTheme', videoTheme);
  }, [videoTheme]);

  const setVideoTheme = (t) => setVideoThemeState(t);

  const login = (token) => {
    localStorage.setItem('token', token);
    setAuthed(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setAuthed(false);
  };

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await api.get('/time/active');
      setActiveSession(data || null);
    } catch (e) {
      // 401 handled globally by the api interceptor; log anything else
      if (e.response?.status !== 401) console.error('Failed to refresh active time session', e);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    refreshSession();
    api.get('/stages').then(({ data }) => setStagesData(data)).catch((e) => {
      if (e.response?.status !== 401) console.error('Failed to load stage config', e);
    });
  }, [authed, refreshSession]);

  useEffect(() => {
    clearInterval(tickRef.current);
    if (activeSession?.clock_in) {
      const update = () => {
        const now = Date.now();
        const gross = now - new Date(activeSession.clock_in).getTime();
        setElapsed(Math.max(0, Math.floor((gross - pausedMs(activeSession, now)) / 1000)));
      };
      update();
      tickRef.current = setInterval(update, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(tickRef.current);
  }, [activeSession]);

  const clockIn = async (category, product_id, notes) => {
    const { data } = await api.post('/time/clock-in', { category, product_id: product_id || null, notes: notes || '' });
    setActiveSession(data);
    return data;
  };

  const clockOut = async (notes) => {
    const { data } = await api.post('/time/clock-out', { notes: notes || '' });
    setActiveSession(null);
    return data;
  };

  const pauseSession = async () => {
    const { data } = await api.post('/time/pause');
    setActiveSession(data);
    return data;
  };

  const resumeSession = async () => {
    const { data } = await api.post('/time/resume');
    setActiveSession(data);
    return data;
  };

  return (
    <AppContext.Provider value={{ authed, login, logout, theme, setTheme, videoTheme, setVideoTheme, stagesData, activeSession, elapsed, isPaused, clockIn, clockOut, pauseSession, resumeSession, refreshSession }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
